<template>
  <div class="container py-4">
    <div class="d-flex align-items-center mb-4">
      <i class="bi bi-wallet2 fs-4 me-2 text-success"></i>
      <h2 class="mb-0">Ingresos</h2>
    </div>

    <!-- Tabs -->
    <ul class="nav nav-tabs mb-4">
      <li class="nav-item">
        <button class="nav-link" :class="{ active: tab === 'puntuales' }" @click="tab = 'puntuales'">
          <i class="bi bi-cash-coin me-1"></i>Ingresos
          <span class="badge rounded-pill ms-1" :class="ingresos.length > 0 ? 'bg-success' : 'bg-secondary'">{{ ingresos.length }}</span>
        </button>
      </li>
      <li class="nav-item">
        <button class="nav-link" :class="{ active: tab === 'recurrentes' }" @click="tab = 'recurrentes'">
          <i class="bi bi-arrow-repeat me-1"></i>Recurrentes
          <span class="badge rounded-pill ms-1" :class="recurrentes.length > 0 ? 'bg-success' : 'bg-secondary'">{{ recurrentes.length }}</span>
        </button>
      </li>
    </ul>

    <!-- ===================== TAB INGRESOS ===================== -->
    <div v-if="tab === 'puntuales'">
      <!-- Formulario Nuevo/Editar Ingreso (card como Nuevo Gasto) -->
      <div v-if="mostrarFormIngreso" class="card mb-4 border-success">
        <div class="card-header bg-success text-white py-2">
          <h5 class="mb-0">
            <i class="bi bi-cash-coin me-2"></i>
            {{ editandoIngreso ? 'Editar Ingreso' : 'Nuevo Ingreso' }}
          </h5>
        </div>
        <div class="card-body">
          <form @submit.prevent="guardarIngreso">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Fecha *</label>
                <input type="date" class="form-control" v-model="formIngreso.fecha" required />
              </div>
              <div class="col-md-6 position-relative">
                <label class="form-label">Concepto *</label>
                <div class="position-relative">
                  <input
                    type="text"
                    class="form-control"
                    v-model="formIngreso.concepto"
                    @input="showConceptosIngreso = true"
                    @blur="hideConceptosIngreso"
                    placeholder="Ej: Nómina enero"
                    required
                  />
                  <div v-if="showConceptosIngreso && conceptosIngresoFiltrados.length" class="suggestions-ingreso">
                    <div
                      v-for="sug in conceptosIngresoFiltrados"
                      :key="sug.concepto"
                      class="suggestion-item"
                      @mousedown.prevent="selectConceptoIngreso(sug)"
                    >
                      <span>{{ sug.concepto }}</span>
                      <small class="text-muted ms-2">{{ sug.categoria }}</small>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label">Categoría</label>
                <select class="form-select" v-model="formIngreso.categoria">
                  <option v-for="cat in categoriasIngresos" :key="cat" :value="cat">{{ cat }}</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Cantidad (€) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  class="form-control"
                  v-model="formIngreso.cantidad"
                  placeholder="0.00"
                  required
                />
              </div>
              <div class="col-12" v-if="errorIngreso">
                <div class="alert alert-danger py-2 mb-0">{{ errorIngreso }}</div>
              </div>
              <div class="col-12">
                <button type="submit" class="btn btn-success" :disabled="guardandoIngreso">
                  <span v-if="guardandoIngreso" class="spinner-border spinner-border-sm me-1"></span>
                  {{ editandoIngreso ? 'Actualizar' : 'Guardar Ingreso' }}
                </button>
                <button type="button" class="btn btn-outline-secondary ms-2" @click="cerrarFormIngreso">
                  Cancelar
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">Lista de Ingresos</h5>
        <button class="btn btn-success btn-sm" @click="abrirFormIngreso()">
          <i class="bi bi-plus-circle-fill me-1"></i>Nuevo Ingreso
        </button>
      </div>

      <!-- Resumen total -->
      <div v-if="ingresos.length > 0" class="alert alert-success py-2 d-flex justify-content-between align-items-center">
        <span><i class="bi bi-graph-up me-1"></i>Total ingresos</span>
        <strong>{{ formatCurrency(totalIngresos) }}</strong>
      </div>

      <div v-if="cargando" class="text-center py-4">
        <div class="spinner-border text-success"></div>
      </div>
      <div v-else-if="ingresos.length === 0" class="text-center py-5 text-muted">
        <i class="bi bi-inbox fs-1 d-block mb-2"></i>
        No hay ingresos registrados. Añade tu primer ingreso.
      </div>
      <div v-else class="table-responsive">
        <table class="table table-hover table-sm">
          <thead class="table-light">
            <tr>
              <th>Fecha</th>
              <th>Concepto</th>
              <th>Categoría</th>
              <th class="text-end">Cantidad</th>
              <th class="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ing in ingresosOrdenados" :key="ing.id">
              <td class="text-nowrap">{{ formatFecha(ing.fecha) }}</td>
              <td>{{ ing.concepto }}</td>
              <td><span class="badge bg-success-subtle text-success-emphasis">{{ ing.categoria }}</span></td>
              <td class="text-end fw-semibold text-success">{{ formatCurrency(ing.cantidad) }}</td>
              <td class="text-center text-nowrap">
                <button class="btn btn-sm btn-warning me-1" @click="abrirFormIngreso(ing)" title="Editar">
                  <i class="bi bi-pencil"></i>
                </button>
                <button class="btn btn-sm btn-danger" @click="eliminarIngreso(ing)" title="Eliminar">
                  <i class="bi bi-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ===================== TAB RECURRENTES ===================== -->
    <div v-if="tab === 'recurrentes'">
      <!-- Formulario Nuevo/Editar Recurrente (card como Nuevo Gasto) -->
      <div v-if="mostrarFormRecurrente" class="card mb-4 border-success">
        <div class="card-header bg-success text-white py-2">
          <h5 class="mb-0">
            <i class="bi bi-arrow-repeat me-2"></i>
            {{ editandoRecurrente ? 'Editar Recurrente' : 'Nuevo Recurrente' }}
          </h5>
        </div>
        <div class="card-body">
          <form @submit.prevent="guardarRecurrente">
            <div class="row g-3">
              <div class="col-md-6 position-relative">
                <label class="form-label">Concepto *</label>
                <div class="position-relative">
                  <input
                    type="text"
                    class="form-control"
                    v-model="formRecurrente.concepto"
                    @input="showConceptosRecurrente = true"
                    @blur="hideConceptosRecurrente"
                    placeholder="Ej: Nómina"
                    required
                  />
                  <div v-if="showConceptosRecurrente && conceptosRecurrenteFiltrados.length" class="suggestions-ingreso">
                    <div
                      v-for="sug in conceptosRecurrenteFiltrados"
                      :key="sug.concepto"
                      class="suggestion-item"
                      @mousedown.prevent="selectConceptoRecurrente(sug)"
                    >
                      <span>{{ sug.concepto }}</span>
                      <small class="text-muted ms-2">{{ sug.categoria }}</small>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label">Categoría</label>
                <select class="form-select" v-model="formRecurrente.categoria">
                  <option v-for="cat in categoriasIngresos" :key="cat" :value="cat">{{ cat }}</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Cantidad (€) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  class="form-control"
                  v-model="formRecurrente.cantidad"
                  placeholder="0.00"
                  required
                />
              </div>
              <div class="col-md-6">
                <label class="form-label">Periodicidad</label>
                <select class="form-select" v-model="formRecurrente.periodicidad">
                  <option>Mensual</option>
                  <option>Anual</option>
                  <option>Semanal</option>
                  <option>Trimestral</option>
                </select>
              </div>
              <div class="col-md-6" v-if="formRecurrente.periodicidad === 'Anual'">
                <label class="form-label">Mes (1-12)</label>
                <select class="form-select" v-model.number="formRecurrente.mes">
                  <option v-for="m in 12" :key="m" :value="m">{{ ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'][m-1] }}</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">{{ formRecurrente.periodicidad === 'Semanal' ? 'Día semana (1=Lu, 7=Do)' : 'Día del mes' }}</label>
                <input type="number" :min="1" :max="formRecurrente.periodicidad === 'Semanal' ? 7 : 31" class="form-control" v-model.number="formRecurrente.dia" />
              </div>
              <div class="col-12">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" id="activoCheck" v-model="formRecurrente.activo" />
                  <label class="form-check-label" for="activoCheck">Activo</label>
                </div>
              </div>
              <div class="col-12" v-if="errorRecurrente">
                <div class="alert alert-danger py-2 mb-0">{{ errorRecurrente }}</div>
              </div>
              <div class="col-12">
                <button type="submit" class="btn btn-success" :disabled="guardandoRecurrente">
                  <span v-if="guardandoRecurrente" class="spinner-border spinner-border-sm me-1"></span>
                  {{ editandoRecurrente ? 'Actualizar' : 'Guardar Recurrente' }}
                </button>
                <button type="button" class="btn btn-outline-secondary ms-2" @click="cerrarFormRecurrente">
                  Cancelar
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 class="mb-0">Ingresos Recurrentes</h5>
          <small class="text-muted">Se insertan automáticamente en la tabla de ingresos cada día a las 5:00</small>
        </div>
        <button class="btn btn-success btn-sm" @click="abrirFormRecurrente()">
          <i class="bi bi-plus-circle-fill me-1"></i>Nuevo Recurrente
        </button>
      </div>

      <!-- Resumen mensual -->
      <div v-if="recurrentes.length > 0" class="alert alert-success py-2 d-flex justify-content-between align-items-center">
        <span><i class="bi bi-calendar-month me-1"></i>Total mensual estimado (activos)</span>
        <strong>{{ formatCurrency(totalMensualEstimado) }}</strong>
      </div>

      <div v-if="cargandoRec" class="text-center py-4">
        <div class="spinner-border text-success"></div>
      </div>
      <div v-else-if="recurrentes.length === 0" class="text-center py-5 text-muted">
        <i class="bi bi-arrow-repeat fs-1 d-block mb-2"></i>
        No hay ingresos recurrentes. Añade tu nómina, alquiler...
      </div>
      <div v-else class="table-responsive">
        <table class="table table-hover table-sm">
          <thead class="table-light">
            <tr>
              <th>Concepto</th>
              <th>Categoría</th>
                <th>Periodicidad</th>
                <th>Día</th>
                <th class="text-end">Cantidad</th>
                <th>Última inserción</th>
              <th class="text-center">Estado</th>
              <th class="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="rec in recurrentes" :key="rec.id" :class="{ 'text-muted': !rec.activo }">
              <td>{{ rec.concepto }}</td>
              <td><span class="badge bg-success-subtle text-success-emphasis">{{ rec.categoria }}</span></td>
              <td>{{ rec.periodicidad }}</td>
              <td>{{ rec.dia }}</td>
              <td class="text-end fw-semibold" :class="rec.activo ? 'text-success' : 'text-muted'">{{ formatCurrency(rec.cantidad) }}</td>
              <td class="small">{{ rec.ultima_insercion ? formatFecha(rec.ultima_insercion) : '-' }}</td>
              <td class="text-center">
                <span class="badge" :class="rec.activo ? 'bg-success' : 'bg-secondary'">
                  {{ rec.activo ? 'Activo' : 'Inactivo' }}
                </span>
              </td>
              <td class="text-center text-nowrap">
                <button class="btn btn-sm btn-warning me-1" @click="abrirFormRecurrente(rec)" title="Editar">
                  <i class="bi bi-pencil"></i>
                </button>
                <button class="btn btn-sm btn-danger" @click="eliminarRecurrente(rec)" title="Eliminar">
                  <i class="bi bi-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import axios from '../config/axios';

const CATEGORIAS = ['Nómina', 'Alquiler', 'Inversiones', 'Freelance', 'Pensión', 'Otros'];

const formVacioIngreso = () => ({
  fecha: new Date().toISOString().split('T')[0],
  concepto: '',
  categoria: 'Nómina',
  cantidad: ''
});

const formVacioRecurrente = () => ({
  concepto: '',
  categoria: 'Nómina',
  cantidad: '',
  periodicidad: 'Mensual',
  dia: 1,
  mes: 1,
  activo: true
});

export default {
  name: 'Ingresos',
  setup() {
    const route = useRoute();
    const tab = ref(route.query.tab === 'recurrentes' ? 'recurrentes' : 'puntuales');
    const ingresos = ref([]);
    const recurrentes = ref([]);
    const cargando = ref(false);
    const cargandoRec = ref(false);
    const categoriasIngresos = ref(CATEGORIAS);

    // Form ingreso
    const mostrarFormIngreso = ref(false);
    const editandoIngreso = ref(null);
    const formIngreso = ref(formVacioIngreso());
    const guardandoIngreso = ref(false);
    const errorIngreso = ref('');
    const showConceptosIngreso = ref(false);

    // Form recurrente
    const mostrarFormRecurrente = ref(false);
    const editandoRecurrente = ref(null);
    const formRecurrente = ref(formVacioRecurrente());
    const guardandoRecurrente = ref(false);
    const errorRecurrente = ref('');
    const showConceptosRecurrente = ref(false);

    const ingresosOrdenados = computed(() =>
      [...ingresos.value].sort((a, b) => b.fecha.localeCompare(a.fecha))
    );

    const conceptosIngresoFiltrados = computed(() => {
      if (!formIngreso.value.concepto || formIngreso.value.concepto.length < 2) return [];
      const term = formIngreso.value.concepto.toLowerCase();
      const unicos = [];
      const vistos = new Set();
      ingresos.value.forEach(i => {
        if (i.concepto && i.concepto.toLowerCase().includes(term) && !vistos.has(i.concepto)) {
          vistos.add(i.concepto);
          unicos.push({ concepto: i.concepto, categoria: i.categoria });
        }
      });
      return unicos.slice(0, 5);
    });

    function selectConceptoIngreso(sug) {
      formIngreso.value.concepto = sug.concepto;
      formIngreso.value.categoria = sug.categoria || 'Nómina';
      showConceptosIngreso.value = false;
    }

    function hideConceptosIngreso() {
      setTimeout(() => { showConceptosIngreso.value = false; }, 200);
    }

    const conceptosRecurrenteFiltrados = computed(() => {
      if (!formRecurrente.value.concepto || formRecurrente.value.concepto.length < 2) return [];
      const term = formRecurrente.value.concepto.toLowerCase();
      const unicos = [];
      const vistos = new Set();
      [...recurrentes.value, ...ingresos.value].forEach(i => {
        const c = i.concepto;
        if (c && c.toLowerCase().includes(term) && !vistos.has(c)) {
          vistos.add(c);
          unicos.push({ concepto: c, categoria: i.categoria || 'Nómina' });
        }
      });
      return unicos.slice(0, 5);
    });

    function selectConceptoRecurrente(sug) {
      formRecurrente.value.concepto = sug.concepto;
      formRecurrente.value.categoria = sug.categoria || 'Nómina';
      showConceptosRecurrente.value = false;
    }

    function hideConceptosRecurrente() {
      setTimeout(() => { showConceptosRecurrente.value = false; }, 200);
    }

    const totalIngresos = computed(() =>
      ingresos.value.reduce((s, i) => s + parseFloat(i.cantidad), 0)
    );

    const totalMensualEstimado = computed(() => {
      return recurrentes.value
        .filter(r => r.activo)
        .reduce((s, r) => {
          if (r.periodicidad === 'Mensual') return s + r.cantidad;
          if (r.periodicidad === 'Anual') return s + r.cantidad / 12;
          if (r.periodicidad === 'Trimestral') return s + r.cantidad / 3;
          if (r.periodicidad === 'Semanal') return s + r.cantidad * 4.33;
          return s + r.cantidad;
        }, 0);
    });

    async function cargarIngresos() {
      cargando.value = true;
      try {
        const { data } = await axios.get('/api/ingresos');
        ingresos.value = data;
      } catch { ingresos.value = []; }
      finally { cargando.value = false; }
    }

    async function cargarRecurrentes() {
      cargandoRec.value = true;
      try {
        const { data } = await axios.get('/api/ingresos/recurrentes');
        recurrentes.value = data;
      } catch { recurrentes.value = []; }
      finally { cargandoRec.value = false; }
    }

    function abrirFormIngreso(ing = null) {
      editandoIngreso.value = ing;
      formIngreso.value = ing
        ? { fecha: ing.fecha, concepto: ing.concepto, categoria: ing.categoria, cantidad: ing.cantidad }
        : formVacioIngreso();
      errorIngreso.value = '';
      mostrarFormIngreso.value = true;
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    }

    function cerrarFormIngreso() {
      mostrarFormIngreso.value = false;
      editandoIngreso.value = null;
    }

    async function guardarIngreso() {
      guardandoIngreso.value = true;
      errorIngreso.value = '';
      try {
        if (editandoIngreso.value) {
          const { data } = await axios.put(`/api/ingresos/${editandoIngreso.value.id}`, formIngreso.value);
          const idx = ingresos.value.findIndex(i => i.id === editandoIngreso.value.id);
          if (idx !== -1) ingresos.value[idx] = data;
        } else {
          const { data } = await axios.post('/api/ingresos', formIngreso.value);
          ingresos.value.push(data);
        }
        cerrarFormIngreso();
      } catch (err) {
        errorIngreso.value = err.response?.data?.error || 'Error al guardar';
      } finally {
        guardandoIngreso.value = false;
      }
    }

    async function eliminarIngreso(ing) {
      if (!confirm(`¿Eliminar el ingreso "${ing.concepto}"?`)) return;
      try {
        await axios.delete(`/api/ingresos/${ing.id}`);
        ingresos.value = ingresos.value.filter(i => i.id !== ing.id);
      } catch { alert('Error al eliminar'); }
    }

    function abrirFormRecurrente(rec = null) {
      editandoRecurrente.value = rec;
      formRecurrente.value = rec
        ? { concepto: rec.concepto, categoria: rec.categoria, cantidad: rec.cantidad, periodicidad: rec.periodicidad, dia: rec.dia, mes: rec.mes || 1, activo: !!rec.activo }
        : formVacioRecurrente();
      errorRecurrente.value = '';
      mostrarFormRecurrente.value = true;
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    }

    function cerrarFormRecurrente() {
      mostrarFormRecurrente.value = false;
      editandoRecurrente.value = null;
    }

    async function guardarRecurrente() {
      guardandoRecurrente.value = true;
      errorRecurrente.value = '';
      try {
        const payload = { ...formRecurrente.value, activo: formRecurrente.value.activo ? 1 : 0 };
        if (editandoRecurrente.value) {
          const { data } = await axios.put(`/api/ingresos/recurrentes/${editandoRecurrente.value.id}`, payload);
          const idx = recurrentes.value.findIndex(r => r.id === editandoRecurrente.value.id);
          if (idx !== -1) recurrentes.value[idx] = data;
        } else {
          const { data } = await axios.post('/api/ingresos/recurrentes', payload);
          recurrentes.value.push(data);
        }
        cerrarFormRecurrente();
      } catch (err) {
        errorRecurrente.value = err.response?.data?.error || 'Error al guardar';
      } finally {
        guardandoRecurrente.value = false;
      }
    }

    async function eliminarRecurrente(rec) {
      if (!confirm(`¿Eliminar "${rec.concepto}"?`)) return;
      try {
        await axios.delete(`/api/ingresos/recurrentes/${rec.id}`);
        recurrentes.value = recurrentes.value.filter(r => r.id !== rec.id);
      } catch { alert('Error al eliminar'); }
    }

    function formatCurrency(val) {
      return Number(val).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
    }

    function formatFecha(fecha) {
      if (!fecha) return '';
      const [y, m, d] = fecha.split('-');
      return `${d}/${m}/${y}`;
    }

    onMounted(() => {
      cargarIngresos();
      cargarRecurrentes();
    });

    return {
      tab, ingresos, recurrentes, cargando, cargandoRec,
      categoriasIngresos, ingresosOrdenados, totalIngresos, totalMensualEstimado,
      mostrarFormIngreso, editandoIngreso, formIngreso, guardandoIngreso, errorIngreso,
      showConceptosIngreso, conceptosIngresoFiltrados, selectConceptoIngreso, hideConceptosIngreso,
      showConceptosRecurrente, conceptosRecurrenteFiltrados, selectConceptoRecurrente, hideConceptosRecurrente,
      mostrarFormRecurrente, editandoRecurrente, formRecurrente, guardandoRecurrente, errorRecurrente,
      abrirFormIngreso, cerrarFormIngreso, guardarIngreso, eliminarIngreso,
      abrirFormRecurrente, cerrarFormRecurrente, guardarRecurrente, eliminarRecurrente,
      formatCurrency, formatFecha
    };
  }
};
</script>

<style scoped>
.suggestions-ingreso {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  z-index: 1050;
  max-height: 180px;
  overflow-y: auto;
}

.suggestions-ingreso .suggestion-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
}

.suggestions-ingreso .suggestion-item:hover {
  background-color: rgba(25, 135, 84, 0.1);
}
</style>
